import {
  AnimatedTranscribeIcon,
  Button,
  Tooltip,
  useConfirmModal,
} from '@affine/component';
import { AudioPlayer } from '@affine/component/ui/audio-player';
import { useEnableAI } from '@affine/core/components/hooks/affine/use-enable-ai';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useSeekTime } from '@affine/core/components/hooks/use-seek-time';
import { CurrentServerScopeProvider } from '@affine/core/components/providers/current-server-scope';
import { PublicUserLabel } from '@affine/core/modules/cloud/views/public-user';
import { GlobalDialogService } from '@affine/core/modules/dialogs';
import type { AudioAttachmentBlock } from '@affine/core/modules/media/entities/audio-attachment-block';
import { AudioAttachmentService } from '@affine/core/modules/media/services/audio-attachment';
import { resolveBlobDisplayName } from '@affine/core/utils/blob-display-name';
import { Trans, useI18n } from '@affine/i18n';
import track from '@affine/track';
import {
  ATTACHMENT_MEDIA_PRESENCE_TARGET_ATTRIBUTE,
  type AttachmentMediaFollowOptions,
  type AttachmentMediaPresenceUpdate,
} from '@blocksuite/affine/blocks/attachment';
import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import { ResetIcon } from '@blocksuite/icons/rc';
import type { AttachmentMediaPresenceState } from '@blocksuite/store';
import { useLiveData, useService } from '@toeverything/infra';
import bytes from 'bytes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AttachmentViewerProps } from '../types';
import * as styles from './audio-block.css';
import { TranscriptionBlock } from './transcription-block';

const stopAudioEmbedEvent = (event: React.MouseEvent<HTMLDivElement>) => {
  event.stopPropagation();
};

const noopAudioControl = (event: React.MouseEvent) => {
  event.stopPropagation();
};

const noopAudioSeek = () => {};

const noopPlaybackRateChange = () => {};

function resolveAudioAttachmentDisplayName(
  model: AttachmentBlockModel
): string {
  return resolveBlobDisplayName({
    blobId: model.props.sourceId,
    occurrenceName: model.props.name,
    mimeType: model.props.type,
  });
}

const AUDIO_PRESENCE_TIME_THROTTLE_MS = 2000;
const AUDIO_REMOTE_SEEK_DRIFT_SECONDS = 0.75;

type AudioMediaPresenceTargetElement = HTMLDivElement & {
  applyRemotePresence?: (
    presence: AttachmentMediaPresenceState,
    options: AttachmentMediaFollowOptions
  ) => void;
};

type AudioPresencePublisher = (update: AttachmentMediaPresenceUpdate) => void;

const getRemoteAudioSeekTime = (
  presence: AttachmentMediaPresenceState,
  fallbackDuration: number
) => {
  const elapsedSeconds = presence.playing
    ? ((Date.now() - presence.updatedAt) / 1000) * presence.playbackRate
    : 0;
  const duration = presence.duration || fallbackDuration || 0;
  const nextTime = presence.currentTime + elapsedSeconds;
  if (duration <= 0) {
    return Math.max(0, nextTime);
  }
  return Math.min(Math.max(0, nextTime), duration);
};

const AudioBlockLoadingPlaceholder = ({
  model,
}: {
  model: AttachmentBlockModel;
}) => (
  <AudioPlayer
    className={styles.embeddedPlayer}
    name={resolveAudioAttachmentDisplayName(model)}
    description={<>{bytes(model.props.size)}</>}
    loading
    playbackState="idle"
    waveform={null}
    seekTime={0}
    duration={0}
    onClick={stopAudioEmbedEvent}
    onPlay={noopAudioControl}
    onPause={noopAudioControl}
    onStop={noopAudioControl}
    onSeek={noopAudioSeek}
    playbackRate={1}
    onPlaybackRateChange={noopPlaybackRateChange}
  />
);

const AttachmentAudioPlayer = ({
  block,
  publishMediaPresence,
}: {
  block: AudioAttachmentBlock;
  publishMediaPresence?: AudioPresencePublisher;
}) => {
  const audioMedia = block.audioMedia;
  const playbackState = useLiveData(audioMedia.playbackState$);
  const stats = useLiveData(audioMedia.stats$);
  const expanded = useLiveData(block.expanded$);
  const [preflightChecking, setPreflightChecking] = useState(false);
  const transcribing =
    useLiveData(block.transcriptionJob.transcribing$) || preflightChecking;
  const loading = useLiveData(audioMedia.loading$);
  const loadingError = useLiveData(audioMedia.loadError$);
  const error = useLiveData(block.transcriptionJob.error$);
  const transcribed = useLiveData(block.hasTranscription$);
  const handleClick = useCallback(stopAudioEmbedEvent, []);
  const confirmModal = useConfirmModal();
  const seekTime = useSeekTime(playbackState, stats.duration);
  const lastPresenceAt = useRef(0);
  const presenceTargetRef = useRef<AudioMediaPresenceTargetElement | null>(
    null
  );

  const publishPresence = useCallback(
    (
      action: AttachmentMediaPresenceUpdate['action'],
      options: { force?: boolean; seekTime?: number } = {}
    ) => {
      if (!publishMediaPresence) {
        return;
      }

      const now = Date.now();
      if (
        !options.force &&
        action === 'time' &&
        now - lastPresenceAt.current < AUDIO_PRESENCE_TIME_THROTTLE_MS
      ) {
        return;
      }
      lastPresenceAt.current = now;

      const state = audioMedia.getPlaybackStateData();
      publishMediaPresence({
        action,
        currentTime: options.seekTime ?? audioMedia.getCurrentSeekPosition(),
        duration: stats.duration || undefined,
        mediaKind: 'audio',
        muted: false,
        playbackRate: state.playbackRate || 1,
        playing: state.state === 'playing',
      });
    },
    [audioMedia, publishMediaPresence, stats.duration]
  );

  useEffect(() => {
    const target = presenceTargetRef.current;
    if (!target) {
      return;
    }

    target.setAttribute(ATTACHMENT_MEDIA_PRESENCE_TARGET_ATTRIBUTE, 'true');
    target.applyRemotePresence = (presence, options) => {
      const nextTime = getRemoteAudioSeekTime(presence, stats.duration);
      const nextState = presence.playing ? 'playing' : 'paused';
      const nextRate = presence.playbackRate || 1;
      const currentState = audioMedia.getPlaybackStateData();
      const currentTime = audioMedia.getCurrentSeekPosition();
      const seekDrift = Math.abs(currentTime - nextTime);
      const stateChanged = currentState.state !== nextState;
      const rateChanged =
        Math.abs((currentState.playbackRate || 1) - nextRate) > 0.001;

      audioMedia.setMuted(options.muted);
      if (
        !stateChanged &&
        !rateChanged &&
        seekDrift <= AUDIO_REMOTE_SEEK_DRIFT_SECONDS
      ) {
        return;
      }

      audioMedia.syncState({
        state: nextState,
        seekOffset: nextTime,
        updateTime: Date.now(),
        playbackRate: nextRate,
      });
    };

    return () => {
      delete target.applyRemotePresence;
      target.removeAttribute(ATTACHMENT_MEDIA_PRESENCE_TARGET_ATTRIBUTE);
    };
  }, [audioMedia, stats.duration]);

  useEffect(() => {
    if (playbackState?.state !== 'playing') {
      return;
    }
    publishPresence('time', { seekTime });
  }, [playbackState?.state, publishPresence, seekTime]);

  const handlePlay = useCallback(() => {
    audioMedia?.play();
    publishPresence('play', { force: true });
  }, [audioMedia, publishPresence]);

  const handlePause = useCallback(() => {
    audioMedia?.pause();
    publishPresence('pause', { force: true });
  }, [audioMedia, publishPresence]);

  const handleStop = useCallback(() => {
    audioMedia?.stop();
    publishPresence('pause', { force: true, seekTime: 0 });
  }, [audioMedia, publishPresence]);

  const handleSeek = useCallback(
    (time: number) => {
      audioMedia?.seekTo(time);
      publishPresence('seek', { force: true, seekTime: time });
    },
    [audioMedia, publishPresence]
  );

  const handlePlaybackRateChange = useCallback(
    (rate: number) => {
      audioMedia?.setPlaybackRate(rate);
      publishPresence('rate', { force: true });
    },
    [audioMedia, publishPresence]
  );

  const reload = useCallback(() => {
    audioMedia?.revalidateBuffer();
  }, [audioMedia]);

  const t = useI18n();

  const enableAi = useEnableAI();

  const globalDialogService = useService(GlobalDialogService);

  const handleNotesClick = useAsyncCallback(async () => {
    if (!enableAi || transcribing) {
      return;
    }

    if (transcribed) {
      block.expanded$.setValue(!expanded);
      track.doc.editor.audioBlock.openTranscribeNotes({
        type: 'Meeting record',
        method: 'success',
        option: expanded ? 'off' : 'on',
      });
      return;
    }

    if (!block.transcriptionJob.currentUserId) {
      confirmModal.openConfirmModal({
        title: t['com.streamlain.ai.login-required.dialog-title'](),
        description: t['com.streamlain.ai.login-required.dialog-content'](),
        confirmText: t['com.streamlain.ai.login-required.dialog-confirm'](),
        confirmButtonOptions: {
          variant: 'primary',
        },
        cancelText: t['com.streamlain.ai.login-required.dialog-cancel'](),
        onConfirm: () => {
          globalDialogService.open('sign-in', {});
        },
      });
      track.doc.editor.audioBlock.openTranscribeNotes({
        type: 'Meeting record',
        method: 'not signed in',
      });
      return;
    }

    setPreflightChecking(true);
    const result = await block.transcriptionJob.preflightCheck();
    setPreflightChecking(false);
    if (result?.error === 'created-by-others') {
      confirmModal.openConfirmModal({
        title: t['com.streamlain.audio.transcribe.non-owner.confirm.title'](),
        description: (
          <Trans i18nKey="com.streamlain.audio.transcribe.non-owner.confirm.message">
            Please contact <PublicUserLabel id={result.userId} /> to upgrade AI
            rights or resend the attachment.
          </Trans>
        ),
        onCancel: false,
        confirmText: t['Confirm'](),
        confirmButtonOptions: {
          variant: 'primary',
        },
      });
      track.doc.editor.audioBlock.openTranscribeNotes({
        type: 'Meeting record',
        method: 'not owner',
      });
    } else {
      await block.transcribe();
      track.doc.editor.audioBlock.transcribeRecording({
        type: 'Meeting record',
        method: 'success',
        option: 'handle transcribing',
      });
    }
  }, [
    enableAi,
    transcribing,
    transcribed,
    block,
    expanded,
    confirmModal,
    t,
    globalDialogService,
  ]);

  const notesEntry = useMemo(() => {
    if (!enableAi) {
      return null;
    }
    const inner = (
      <Button
        variant="plain"
        prefix={
          <AnimatedTranscribeIcon
            state={transcribing ? 'transcribing' : 'idle'}
          />
        }
        size="large"
        prefixClassName={styles.notesButtonIcon}
        className={styles.notesButton}
        onClick={handleNotesClick}
      >
        {transcribing
          ? t['com.streamlain.audio.transcribing']()
          : t['com.streamlain.audio.notes']()}
      </Button>
    );
    if (transcribing) {
      return (
        <Tooltip content={t['com.streamlain.audio.transcribing']()}>
          {inner}
        </Tooltip>
      );
    }
    return inner;
  }, [enableAi, transcribing, handleNotesClick, t]);

  const descriptionEntry = useMemo(() => {
    if (loadingError) {
      return (
        <>
          <div className={styles.error}>{loadingError.message}</div>
          <button className={styles.reloadButton} onClick={reload}>
            <ResetIcon className={styles.reloadButtonIcon} />
            Reload
          </button>
        </>
      );
    }

    if (!loading && error) {
      return <div className={styles.error}>{error.message}</div>;
    }

    return <>{bytes(block.props.props.size)}</>;
  }, [loading, loadingError, error, reload, block.props.props.size]);

  return (
    <div ref={presenceTargetRef} className={styles.mediaPresenceTarget}>
      <AudioPlayer
        className={styles.embeddedPlayer}
        name={resolveAudioAttachmentDisplayName(block.props)}
        description={descriptionEntry}
        loading={stats.duration === 0}
        playbackState={playbackState?.state || 'idle'}
        waveform={stats.waveform}
        seekTime={seekTime}
        duration={stats.duration}
        onClick={handleClick}
        onPlay={handlePlay}
        onPause={handlePause}
        onStop={handleStop}
        onSeek={handleSeek}
        playbackRate={playbackState?.playbackRate || 1.0}
        onPlaybackRateChange={handlePlaybackRateChange}
        notesEntry={
          <CurrentServerScopeProvider>{notesEntry}</CurrentServerScopeProvider>
        }
      />
    </div>
  );
};

const useAttachmentMediaBlock = (model: AttachmentBlockModel) => {
  const audioAttachmentService = useService(AudioAttachmentService);
  const [audioAttachmentBlock, setAttachmentMedia] = useState<
    AudioAttachmentBlock | undefined
  >(undefined);

  useEffect(() => {
    if (!model.props.sourceId) {
      return;
    }
    const entity = audioAttachmentService.get(model);
    if (!entity) {
      return;
    }
    const audioAttachmentBlock = entity.obj;
    setAttachmentMedia(audioAttachmentBlock);
    audioAttachmentBlock.mount();
    return () => {
      audioAttachmentBlock.unmount();
      entity.release();
    };
  }, [audioAttachmentService, model]);
  return audioAttachmentBlock;
};

export const AudioBlockEmbedded = ({
  model,
  publishMediaPresence,
}: AttachmentViewerProps & {
  publishMediaPresence?: AudioPresencePublisher;
}) => {
  const audioAttachmentBlock = useAttachmentMediaBlock(model);
  const transcriptionBlock = useLiveData(
    audioAttachmentBlock?.transcriptionBlock$
  );
  const expanded = useLiveData(audioAttachmentBlock?.expanded$);
  return (
    <div className={styles.root}>
      {audioAttachmentBlock ? (
        <AttachmentAudioPlayer
          block={audioAttachmentBlock}
          publishMediaPresence={publishMediaPresence}
        />
      ) : (
        <AudioBlockLoadingPlaceholder model={model} />
      )}
      {transcriptionBlock && expanded && (
        <TranscriptionBlock block={transcriptionBlock} />
      )}
    </div>
  );
};
